import { UserOutlined } from '@ant-design/icons'
import { Audience } from '@models/Audience'
import { LoyaltyTier } from '@models/LoyaltyTier'
import { fetchUsersByLoyaltyTier } from '@services/AudiencesService'
import { selectAudienceById } from '@store/AudiencesReducer'
import { useAppSelector } from '@store/hooks'
import { selectLoyaltyTierById } from '@store/LoyaltyTierReducer'
import { numberFormat } from '@utils/currencyFormatter'
import { Skeleton, Tag, Tooltip } from 'antd'
import { FC, useEffect, useState } from 'react'

type Props = {
  audience?: Audience
  loyaltyTier?: LoyaltyTier
  audienceId?: number
  loyaltyTierId?: number
  showOnlySubscribedCount?: boolean
}

const AudienceUsersCount: FC<Props> = ({
  audience,
  loyaltyTier,
  audienceId,
  loyaltyTierId,
  showOnlySubscribedCount = false,
}: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [loyaltyTierCount, setLoyaltyTierCount] = useState<number | undefined>(
    undefined
  )

  const audienceItem = audience
    ? audience
    : useAppSelector(selectAudienceById(audienceId!))

  const loyaltyTierItem = loyaltyTier
    ? loyaltyTier
    : useAppSelector(selectLoyaltyTierById(loyaltyTierId!))

  // Loyalty tiers have no cached count on the model, so they still need a live
  // fetch. Audiences do NOT: their counts ride along in the list payload (see
  // below), which avoids one request per card (an N+1 storm on long lists).
  useEffect(() => {
    if (!loyaltyTierItem) return

    const params = showOnlySubscribedCount
      ? { subscribed_to_notification: true as true } // Force `true` to avoid boolean typing
      : undefined

    setIsLoading(true)
    fetchUsersByLoyaltyTier(loyaltyTierItem.id, params)
      .then((data) => setLoyaltyTierCount(data.data.users))
      .finally(() => setIsLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loyaltyTierItem?.id, showOnlySubscribedCount])

  if (!audienceItem && !loyaltyTierItem) return <></>

  const color = audienceItem?.color ?? loyaltyTierItem?.color

  // Audience counts come straight from the (cached) list payload — no per-card
  // request. Loyalty-tier counts come from the fetch above.
  const count = audienceItem
    ? showOnlySubscribedCount
      ? audienceItem.subscribed_users_count
      : audienceItem.total_users_count
    : loyaltyTierCount

  if (isLoading) {
    return <Skeleton.Node style={{ width: 40, height: 20 }} active />
  }

  // Not computed yet (the server-side cron hasn't run for this segment): show a
  // dash rather than a never-ending loader.
  if (count == null) {
    return (
      <Tooltip title="Not computed yet">
        <Tag color={color}>
          <UserOutlined /> —
        </Tag>
      </Tooltip>
    )
  }

  return (
    <Tag color={color}>
      <UserOutlined /> {numberFormat(count)}
    </Tag>
  )
}

type AudienceUsersCountType = { UsersCount: typeof AudienceUsersCount }

export { AudienceUsersCount, type AudienceUsersCountType }
